import React from 'react';

import { uniq } from 'lodash-es';

import Button from '@/components/ui/buttons/Button';
import ErrorList from '@/components/ui/forms/ErrorList';
import Field from '@/components/ui/forms/Field';
import FormGroup from '@/components/ui/forms/FormGroup';
import Input from '@/components/ui/forms/Input';
import LabelWithStatus from '@/components/ui/forms/LabelWithStatus';
import PasswordInput from '@/components/ui/forms/PasswordInput';
import { validationSchema } from '@/features/registration/schema/validationSchema';
import useReactHookForm from '@/hooks/common/useReactHookForm';

import styles from './InvitationRegisterInputForm.module.scss';

export interface InvitationRegisterFormValues {
  email: string;
  code: string;
  password: string;
  password_confirmation: string;
  first_name: string;
  last_name: string;
  first_name_kana: string;
  last_name_kana: string;
}

interface Props {
  email: string;
  code: string;
  defaultFormValues?: InvitationRegisterFormValues;
  onSubmit: (values: InvitationRegisterFormValues) => void;
}

const InvitationRegisterInputForm: React.FC<Props> = ({
  email,
  code,
  defaultFormValues,
  onSubmit,
}) => {
  const defaultValues: InvitationRegisterFormValues = {
    email,
    code,
    password: '',
    password_confirmation: '',
    first_name: defaultFormValues?.first_name ?? '',
    last_name: defaultFormValues?.last_name ?? '',
    first_name_kana: defaultFormValues?.first_name_kana ?? '',
    last_name_kana: defaultFormValues?.last_name_kana ?? '',
  };

  const {
    registerWithInnerRef,
    handleSubmit,
    formState: {
      isValid,
      isSubmitting,
      touchedFields,
      validFields,
      errorMessages,
    },
    trigger,
  } = useReactHookForm<InvitationRegisterFormValues>({
    validationSchema: validationSchema,
    defaultValues,
  });

  const inputDisabled = isSubmitting;
  const buttonDisabled = !isValid || isSubmitting;

  const passwordErrors = uniq([
    ...(errorMessages.password || []),
    ...(errorMessages.password_confirmation || []),
  ]);

  const nameErrors = [
    ...(errorMessages.last_name || []),
    ...(errorMessages.first_name || []),
  ];

  const nameKanaErrors = [
    ...(errorMessages.last_name_kana || []),
    ...(errorMessages.first_name_kana || []),
  ];

  const submitHandler = handleSubmit(onSubmit);

  return (
    <>
      <div className={styles.InvitationRegisterInputForm__Description}>
        メールアドレスとパスワードは、ログインする時に必要になります。
        {'\n'}
        お手元に控えてください。
      </div>
      <form
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={submitHandler}
        className={styles.InvitationRegisterInputForm}
      >
        <FormGroup className={styles.FormGroup}>
          <LabelWithStatus
            required
            valid={validFields.first_name && validFields.last_name}
          >
            保護者氏名
          </LabelWithStatus>
          <Field isFullWidth>
            <div className={styles.FieldGroup__TwoLines}>
              <Field className={styles.Field} isFullWidth>
                <Input
                  type="text"
                  {...registerWithInnerRef('last_name')}
                  disabled={inputDisabled}
                  placeholder="例）山田"
                />
              </Field>
              <Field className={styles.Field} isFullWidth>
                <Input
                  type="text"
                  {...registerWithInnerRef('first_name')}
                  disabled={inputDisabled}
                  placeholder="例）太郎"
                />
              </Field>
            </div>
            <ErrorList errors={nameErrors} />
          </Field>
        </FormGroup>
        <FormGroup className={styles.FormGroup}>
          <LabelWithStatus
            required
            valid={validFields.first_name_kana && validFields.last_name_kana}
          >
            保護者氏名（かな）
          </LabelWithStatus>
          <Field isFullWidth>
            <div className={styles.FieldGroup__TwoLines}>
              <Field className={styles.Field} isFullWidth>
                <Input
                  type="text"
                  {...registerWithInnerRef('last_name_kana')}
                  disabled={inputDisabled}
                  placeholder="例）やまだ"
                />
              </Field>
              <Field className={styles.Field} isFullWidth>
                <Input
                  type="text"
                  {...registerWithInnerRef('first_name_kana')}
                  disabled={inputDisabled}
                  placeholder="例）たろう"
                />
              </Field>
            </div>
            <ErrorList errors={nameKanaErrors} />
          </Field>
        </FormGroup>
        <div className={styles.InvitationRegisterInputForm__EmailForm}>
          <span
            className={
              styles.InvitationRegisterInputForm__EmailForm__EmailLabel
            }
          >
            メールアドレス
          </span>
          <div className={styles.InvitationRegisterInputForm__EmailForm__Email}>
            {email}
          </div>
        </div>
        <FormGroup className={styles.FormGroup}>
          <LabelWithStatus
            required
            valid={validFields.password && validFields.password_confirmation}
          >
            パスワード
          </LabelWithStatus>
          <Field isFullWidth>
            <PasswordInput
              {...registerWithInnerRef('password', {
                onChange: () => {
                  if (touchedFields.password_confirmation) {
                    void trigger('password_confirmation');
                  }
                },
              })}
              disabled={inputDisabled}
              autoComplete="new-password"
              placeholder="パスワードを入力"
            />
          </Field>
          <Field isFullWidth>
            <PasswordInput
              {...registerWithInnerRef('password_confirmation')}
              disabled={inputDisabled}
              autoComplete="new-password"
              placeholder="パスワードを入力（確認用）"
            />
            <ErrorList errors={passwordErrors} />
            <ul className={styles.Comment}>
              <li className={styles.Comment__Warning}>
                <p>最低6文字以上にしてください。</p>
              </li>
              <li className={styles.Comment__Warning}>
                <p>
                  半角アルファベットと半角数字をそれぞれ1文字以上入れてください。
                </p>
              </li>
              <li>
                <p>
                  名前＋生年月日などは推測されやすいため、英数字以外の記号組み合わせを推奨します。
                </p>
              </li>
            </ul>
          </Field>
        </FormGroup>
        <div className={styles.SubmitButtonArea}>
          <Button
            className={styles.SubmitButton}
            color="primary"
            type="submit"
            disabled={buttonDisabled}
          >
            確認画面へ
          </Button>
        </div>
      </form>
    </>
  );
};

export default InvitationRegisterInputForm;
